import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ErrorState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Library, Upload, Download, Search, MoreVertical, Pencil, RefreshCw, History, Archive, ArchiveRestore,
  FolderCog, Plus, Trash2, Info, FileText, FileSpreadsheet, FileImage, File as FileIcon, Presentation,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

// Uploads/downloads can be larger than the app-wide 15s axios timeout allows.
const FILE_TIMEOUT = 120000;
const ACCEPT = ".pdf,.doc,.docx,.odt,.rtf,.txt,.xls,.xlsx,.ods,.csv,.ppt,.pptx,.png,.jpg,.jpeg,.webp";

const FILE_STYLE = {
  doc: { icon: FileText, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  docx: { icon: FileText, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  odt: { icon: FileText, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  rtf: { icon: FileText, cls: "bg-blue-50 text-blue-700 border-blue-200" },
  txt: { icon: FileText, cls: "bg-slate-50 text-slate-600 border-slate-200" },
  pdf: { icon: FileText, cls: "bg-red-50 text-red-700 border-red-200" },
  xls: { icon: FileSpreadsheet, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  xlsx: { icon: FileSpreadsheet, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ods: { icon: FileSpreadsheet, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  csv: { icon: FileSpreadsheet, cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ppt: { icon: Presentation, cls: "bg-orange-50 text-orange-700 border-orange-200" },
  pptx: { icon: Presentation, cls: "bg-orange-50 text-orange-700 border-orange-200" },
  png: { icon: FileImage, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  jpg: { icon: FileImage, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  jpeg: { icon: FileImage, cls: "bg-violet-50 text-violet-700 border-violet-200" },
  webp: { icon: FileImage, cls: "bg-violet-50 text-violet-700 border-violet-200" },
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtSize = (b) => (!b ? "" : b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);

function FileBadge({ ext }) {
  const s = FILE_STYLE[ext] || { icon: FileIcon, cls: "bg-slate-50 text-slate-600 border-slate-200" };
  const Icon = s.icon;
  return (
    <div className={cn("h-11 w-11 shrink-0 rounded-lg border grid place-items-center relative", s.cls)}>
      <Icon className="h-5 w-5" />
      <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 rounded bg-white border px-1 text-[9px] font-bold uppercase leading-tight">{ext || "file"}</span>
    </div>
  );
}

const EMPTY_FORM = { title: "", category: "", description: "", tags: "", file: null };

export default function LetterLibrary() {
  const { isSuperAdmin } = useAuth();
  const qc = useQueryClient();
  const [activeCat, setActiveCat] = useState("");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [replaceItem, setReplaceItem] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceNote, setReplaceNote] = useState("");
  const [historyItem, setHistoryItem] = useState(null);
  const [catsOpen, setCatsOpen] = useState(false);
  const [newCat, setNewCat] = useState("");

  const cats = useQuery({ queryKey: ["libraryCats"], queryFn: async () => (await api.get("/library/categories")).data });
  const items = useQuery({
    queryKey: ["library", showArchived],
    queryFn: async () => (await api.get("/library", { params: { archived: showArchived } })).data,
  });

  const refresh = () => { qc.invalidateQueries({ queryKey: ["library"] }); qc.invalidateQueries({ queryKey: ["libraryCats"] }); };

  // Search + category filtering is done client-side — the whole library is a
  // few hundred records at most, so this keeps typing instant.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items.data || []).filter((i) =>
      (!activeCat || i.category === activeCat) &&
      (!q || [i.title, i.description, i.file_name, i.category, ...(i.tags || [])].join(" ").toLowerCase().includes(q)));
  }, [items.data, activeCat, search]);

  const grouped = useMemo(() => {
    const order = (cats.data || []).map((c) => c.name);
    const map = new Map();
    for (const i of visible) { if (!map.has(i.category)) map.set(i.category, []); map.get(i.category).push(i); }
    return [...map.entries()].sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
  }, [visible, cats.data]);

  const upload = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", form.file); fd.append("title", form.title.trim()); fd.append("category", form.category);
      fd.append("description", form.description); fd.append("tags", form.tags);
      return (await api.post("/library", fd, { timeout: FILE_TIMEOUT })).data;
    },
    onSuccess: () => { toast.success("Added to the library"); setUploadOpen(false); setForm(EMPTY_FORM); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const saveEdit = useMutation({
    mutationFn: async () => (await api.patch(`/library/${editItem.id}`, {
      title: editForm.title.trim(), category: editForm.category, description: editForm.description,
      tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
    })).data,
    onSuccess: () => { toast.success("Details updated"); setEditItem(null); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const doReplace = useMutation({
    mutationFn: async () => {
      const fd = new FormData(); fd.append("file", replaceFile); fd.append("change_note", replaceNote);
      return (await api.post(`/library/${replaceItem.id}/replace`, fd, { timeout: FILE_TIMEOUT })).data;
    },
    onSuccess: (res) => { toast.success(`Uploaded as version ${res.version}`); setReplaceItem(null); setReplaceFile(null); setReplaceNote(""); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const archive = useMutation({
    mutationFn: async (i) => (await api.delete(`/library/${i.id}`)).data,
    onSuccess: () => { toast.success("Archived — find it under 'Show archived'"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const restore = useMutation({
    mutationFn: async (i) => (await api.post(`/library/${i.id}/restore`)).data,
    onSuccess: () => { toast.success("Restored to the library"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const addCat = useMutation({
    mutationFn: async () => (await api.post("/library/categories", { name: newCat.trim() })).data,
    onSuccess: () => { toast.success("Category added"); setNewCat(""); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const delCat = useMutation({
    mutationFn: async (c) => (await api.delete(`/library/categories/${c.id}`)).data,
    onSuccess: () => { toast.success("Category removed"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const download = async (item, version) => {
    const t = toast.loading("Preparing download…");
    try {
      const res = await api.get(`/library/${item.id}/download`, { responseType: "blob", timeout: FILE_TIMEOUT, params: version ? { version } : {} });
      const h = version ? (item.history || []).find((x) => x.version === version) : item;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = h?.file_name || item.file_name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Downloaded", { id: t });
      if (!version) qc.invalidateQueries({ queryKey: ["library"] });
    } catch { toast.error("Unable to download — try again", { id: t }); }
  };

  const openEdit = (i) => { setEditItem(i); setEditForm({ title: i.title, category: i.category, description: i.description || "", tags: (i.tags || []).join(", "), file: null }); };
  const openUpload = () => { setForm({ ...EMPTY_FORM, category: activeCat || "" }); setUploadOpen(true); };

  const total = (cats.data || []).reduce((s, c) => s + (c.count || 0), 0);
  const canUpload = form.title.trim() && form.category && form.file;

  return (
    <div>
      <PageHeader overline="HR Templates" title="Letter & Forms Library" icon={Library}
        subtitle="One approved master for every HR letter and form. Download, fill in the employee's details, and issue — the same wording every time."
        actions={<>
          <Button variant="outline" onClick={() => setCatsOpen(true)} data-testid="library-cats-btn"><FolderCog className="h-4 w-4 mr-1" /> Categories</Button>
          <Button className="bg-navy" onClick={openUpload} data-testid="library-upload-btn"><Upload className="h-4 w-4 mr-1" /> Add Draft</Button>
        </>}
      />

      <div className="mb-5 flex items-start gap-3 rounded-lg border border-teal/30 bg-teal/5 px-4 py-3 text-sm text-slate-600">
        <Info className="h-4 w-4 mt-0.5 text-teal shrink-0" />
        <p><span className="font-semibold text-navy">How to issue:</span> download the latest version → fill every <span className="font-mono-i text-xs bg-white border rounded px-1">[PLACEHOLDER]</span> (name, Employee ID, date, designation…) → print on letterhead and get it signed → upload the signed copy to that employee's record under <span className="font-medium">Documents</span>. Never edit the master here for a single employee — use <span className="font-medium">Replace file</span> only when the approved wording itself changes.</p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search title, description, tags…" className="pl-9 focus-visible:ring-teal" data-testid="library-search" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600 md:ml-auto">
          <Switch checked={showArchived} onCheckedChange={setShowArchived} data-testid="library-archived-toggle" /> Show archived
        </label>
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button onClick={() => setActiveCat("")}
          className={cn("rounded-full border px-3 py-1 text-sm transition-colors", !activeCat ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-teal")}>
          All <span className="opacity-70">({showArchived ? (items.data || []).length : total})</span>
        </button>
        {(cats.data || []).map((c) => (
          <button key={c.id} onClick={() => setActiveCat(activeCat === c.name ? "" : c.name)}
            className={cn("rounded-full border px-3 py-1 text-sm transition-colors", activeCat === c.name ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-teal")}
            data-testid={`library-cat-${c.name}`}>
            {c.name}{!showArchived && <span className="opacity-70"> ({c.count || 0})</span>}
          </button>
        ))}
      </div>

      {items.isLoading || cats.isLoading ? <Loading /> : items.isError ? (
        <ErrorState message="Couldn't load the library." onRetry={items.refetch} />
      ) : (items.data || []).length === 0 ? (
        showArchived ? <EmptyState icon={Archive} title="No archived items" hint="Archived drafts will appear here and can be restored." />
          : <EmptyState icon={Library} title="The library is empty" hint="Upload your approved draft letters and forms so the whole HR team works from the same versions."
              action={<Button className="bg-navy" onClick={openUpload}><Upload className="h-4 w-4 mr-1" /> Add First Draft</Button>} />
      ) : visible.length === 0 ? (
        <EmptyState icon={Search} title="Nothing matches" hint="Try a different search term or category."
          action={<Button variant="outline" onClick={() => { setSearch(""); setActiveCat(""); }}>Clear filters</Button>} />
      ) : (
        <div className="space-y-8">
          {grouped.map(([cat, list]) => (
            <section key={cat}>
              <h2 className="overline text-slate-500 mb-3">{cat} <span className="text-slate-400">· {list.length}</span></h2>
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {list.map((i) => (
                  <Card key={i.id} className={cn("border-slate-200 shadow-none p-4 flex flex-col gap-3 hover:border-teal/50 transition-colors", i.status === "Archived" && "opacity-70")} data-testid={`library-item-${i.id}`}>
                    <div className="flex items-start gap-3">
                      <FileBadge ext={i.ext} />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-navy leading-snug">{i.title}</p>
                        <p className="text-xs text-slate-400 truncate" title={i.file_name}>{i.file_name} · {fmtSize(i.size)}</p>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0" data-testid={`library-menu-${i.id}`}><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {i.status === "Archived" ? (
                            <DropdownMenuItem onClick={() => restore.mutate(i)}><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</DropdownMenuItem>
                          ) : (<>
                            <DropdownMenuItem onClick={() => openEdit(i)}><Pencil className="h-4 w-4 mr-2" /> Edit details</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => { setReplaceItem(i); setReplaceFile(null); setReplaceNote(""); }}><RefreshCw className="h-4 w-4 mr-2" /> Replace file (new version)</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setHistoryItem(i)}><History className="h-4 w-4 mr-2" /> Version history</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600" onClick={() => { if (window.confirm(`Archive "${i.title}"? It will be hidden from the library but can be restored later.`)) archive.mutate(i); }}>
                              <Archive className="h-4 w-4 mr-2" /> Archive
                            </DropdownMenuItem>
                          </>)}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {i.description && <p className="text-sm text-slate-600 line-clamp-3 whitespace-pre-line">{i.description}</p>}
                    {(i.tags || []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {i.tags.map((t) => (
                          <button key={t} onClick={() => setSearch(t)} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600 hover:bg-teal/10">#{t}</button>
                        ))}
                      </div>
                    )}

                    <div className="mt-auto flex items-end justify-between gap-2 pt-2 border-t border-slate-100">
                      <div className="text-[11px] text-slate-400 leading-tight">
                        <button className="font-semibold text-teal hover:underline" onClick={() => setHistoryItem(i)}>v{i.version}</button>
                        {" · "}Updated {fmtDate(i.updated_at)}
                        <br />by {i.updated_by_name || i.updated_by}{i.downloads ? ` · ${i.downloads} downloads` : ""}
                      </div>
                      <Button size="sm" className="bg-teal hover:bg-teal/90 text-white shrink-0" onClick={() => download(i)} data-testid={`library-download-${i.id}`}>
                        <Download className="h-4 w-4 mr-1" /> Download
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a draft to the library</DialogTitle>
            <DialogDescription>Upload the approved master copy. Use [PLACEHOLDERS] for employee-specific details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Appointment Letter – Lab Technician" data-testid="library-title" /></div>
            <div><Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="library-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
                <SelectContent>{(cats.data || []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>When to use / notes</Label><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. For new technicians after the 6-month probation. Needs Director's signature." /></div>
            <div><Label>Tags <span className="text-slate-400 font-normal">(comma-separated)</span></Label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="technician, probation, lab" /></div>
            <div><Label>File *</Label>
              <Input type="file" accept={ACCEPT} onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} data-testid="library-file" />
              <p className="text-[11px] text-slate-400 mt-1">Word, PDF, Excel, PowerPoint, text or images · max 25 MB. Word (.docx) is best for letters that need editing.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Cancel</Button>
            <Button className="bg-navy" disabled={!canUpload || upload.isPending} onClick={() => upload.mutate()} data-testid="library-upload-submit">
              {upload.isPending ? "Uploading…" : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editItem} onOpenChange={(o) => !o && setEditItem(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Edit details</DialogTitle><DialogDescription>Changes the label only — the file stays the same.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label>Title *</Label><Input value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} /></div>
            <div><Label>Category *</Label>
              <Select value={editForm.category} onValueChange={(v) => setEditForm({ ...editForm, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{(cats.data || []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>When to use / notes</Label><Textarea rows={3} value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} /></div>
            <div><Label>Tags</Label><Input value={editForm.tags} onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button className="bg-navy" disabled={!editForm.title.trim() || saveEdit.isPending} onClick={() => saveEdit.mutate()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Replace dialog */}
      <Dialog open={!!replaceItem} onOpenChange={(o) => !o && setReplaceItem(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Replace file — new version</DialogTitle>
            <DialogDescription>"{replaceItem?.title}" is currently v{replaceItem?.version}. The old file is kept in version history.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>New file *</Label><Input type="file" accept={ACCEPT} onChange={(e) => setReplaceFile(e.target.files?.[0] || null)} /></div>
            <div><Label>What changed?</Label><Input value={replaceNote} onChange={(e) => setReplaceNote(e.target.value)} placeholder="e.g. Notice period updated to 30 days per HR policy" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReplaceItem(null)}>Cancel</Button>
            <Button className="bg-navy" disabled={!replaceFile || doReplace.isPending} onClick={() => doReplace.mutate()}>{doReplace.isPending ? "Uploading…" : `Upload as v${(replaceItem?.version || 0) + 1}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!historyItem} onOpenChange={(o) => !o && setHistoryItem(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Version history</DialogTitle><DialogDescription>{historyItem?.title}</DialogDescription></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {historyItem && [{ ...historyItem, current: true }, ...(historyItem.history || [])].map((v) => (
              <div key={v.version} className={cn("flex items-start gap-3 rounded-md border p-3", v.current ? "border-teal/40 bg-teal/5" : "border-slate-200")}>
                <div className="font-mono-i text-sm font-semibold text-navy w-8">v{v.version}</div>
                <div className="flex-1 min-w-0 text-sm">
                  <p className="text-slate-700">{v.change_note || "—"} {v.current && <span className="ml-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold">CURRENT</span>}</p>
                  <p className="text-[11px] text-slate-400 truncate">{v.file_name} · {fmtDate(v.updated_at)} · {v.updated_by_name || v.updated_by}</p>
                </div>
                <Button size="sm" variant="ghost" onClick={() => download(historyItem, v.current ? undefined : v.version)} title="Download this version"><Download className="h-4 w-4" /></Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Categories dialog */}
      <Dialog open={catsOpen} onOpenChange={setCatsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Categories</DialogTitle><DialogDescription>Add categories as needed.{isSuperAdmin ? " Only empty categories can be removed." : " Only Super Admin can remove categories."}</DialogDescription></DialogHeader>
          <div className="flex gap-2">
            <Input value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="e.g. NDA & Confidentiality" onKeyDown={(e) => e.key === "Enter" && newCat.trim() && addCat.mutate()} />
            <Button className="bg-navy" disabled={!newCat.trim() || addCat.isPending} onClick={() => addCat.mutate()}><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {(cats.data || []).map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded px-2 py-1.5 hover:bg-slate-50 text-sm">
                <span className="text-slate-700">{c.name} <span className="text-slate-400">({c.count || 0})</span></span>
                {isSuperAdmin && (
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-red-600" disabled={c.count > 0}
                    title={c.count > 0 ? "Move or archive its items first" : "Remove category"}
                    onClick={() => window.confirm(`Remove category "${c.name}"?`) && delCat.mutate(c)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
